import { parseDocument } from "htmlparser2";
import { getElementsByTagName } from "domutils";
import type { FilterItem } from "../../../src/taskpane/redux/models/reduxModels";

/**
 * Parsers for the markup `RenderListFilterData` returns — the endpoint SharePoint's own
 * filter pane uses to populate a field's distinct value list.
 *
 * Lifted verbatim out of FileFilterHelper (which still owns the single-library section
 * build and re-exports these) so the aggregate view's cross-library fetcher can reuse
 * them without importing that module's axios/auth/datasource chain. Behaviour is
 * unchanged for the library panel: same functions, same call site, different file.
 *
 * The response comes back in one of two shapes depending on the field: a `<SELECT>` of
 * `<option>`s for choice-like fields, or an `<li>` list for the rest. Callers pick with
 * `data.includes("<SELECT>")`; see pickFilterValueParser.
 */

export const extractSelectOptions = (data: any): FilterItem[] => {
    const options = getElementsByTagName("option", parseDocument(data));

    if (Array.isArray(options)) {
        return options.map((el) => {
            const option: FilterItem = {
                title: el.children[0]['data'],
                value: el.attribs.value,
                selected: false
            }
            return option;
        });
    }

    return [];
}

export const extractListItems = (data: any): FilterItem[] => {
    // splice(1) drops the leading "(All)" entry; every 5th <li> is a value, the rest are
    // the markup SharePoint wraps around it.
    const list = getElementsByTagName("li", parseDocument(data)).splice(1);
    const options: FilterItem[] = [];

    if (Array.isArray(list)) {
        list.forEach((el, index) => {
            if (index % 5 === 0) {
                options.push({
                    title: el.children[0]['data'],
                    value: el.children[0]['data'],
                    selected: false
                })
            }
        });
    }

    return options;
}

/**
 * The shape test both call sites share, kept here so they cannot diverge on it.
 */
export const parseFilterValueMarkup = (data: any): FilterItem[] => {
    if (typeof data !== "string" || data.length === 0) {
        return [];
    }
    return data.includes('<SELECT') ? extractSelectOptions(data) : extractListItems(data);
};
